import { ChangeDetectorRef, Component, HostBinding, inject, Inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import {
    ActivatedRoute,
    IsActiveMatchOptions,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    Router,
    RouterOutlet,
} from '@angular/router';
import { smartTimer } from '@pstg/smart-timer';
import { AuthorizationService, AuthorizerModule, isAllowed } from '@pure/authz-authorizer';
import {
    AlertsCountService,
    ArrayContractStatus,
    CachedCurrentUserService,
    CurrentUser,
    FeatureFlagDxpService,
    IncidentService,
    SubscriptionType,
    UnifiedArray,
    UnifiedArrayService,
} from '@pure1/data';
import { flatMap } from 'lodash';
import moment from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    exhaustMap,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { ampli } from '../../ampli';
import { extractOrgUidFromPath, stripLeadingOrgUid } from '../../data/utils/org-url';
import { WINDOW } from '../../app/injection-tokens';
import { MENU } from '../../app/menu';
import { IMainTab, TabsService } from '../../services/tabs/tabs.service';
import { SupportCaseOrchestratorService } from '../../support/services/support-case-orchestrator.service';
import { TouService } from '../../tou/tou.service';
import { recursiveListItems } from '../../utils/collection';
import { FeatureNames } from '../../model/FeatureNames';
import { AppSwitcherApp, BadgeEmphasis, RootDirective, SvgDirective, ToastService, TooltipDirective } from '@pure/hive';
import { NavMenuIdPipe } from '../pipes/nav-menu-id.pipe';
import { NotificationCenterCounterService } from '@pure/notification-center';
import { RecommendationProcessingService } from '../../service-catalog/recommendations-container/services/recommendation-processing.service';
import { OrgSwitcherService } from './org-switcher/org-switcher.service';
import { AppRailComponent, IConnectedCount } from './app-rail/app-rail.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { PureToastComponent } from '../../ui/components/pure-toast/pure-toast.component';
import { ExportOverlayComponent } from '../../export/export-overlay/export-overlay.component';
import { FeedbackButtonComponent } from '../../nps-survey/feedback-button/feedback-button.component';
import { FeedbackFormComponent } from '../../nps-survey/feedback-form/feedback-form.component';
import { CopilotSidePanelComponent } from '../../copilot/side-panel/copilot-side-panel.component';
import { markedOptionsFactory } from '../../copilot/markedOptionsFactory';
import { CopilotOnboardingModalComponent } from '../../copilot/copilot-onboarding-modal/copilot-onboarding-modal.component';
import {
    CopilotPromptLinkComponent,
    CopilotPromptLinkData,
} from '../../copilot/copilot-prompt-link/copilot-prompt-link.component';
import { CopilotPageSuggestionsComponent } from '../../copilot/copilot-page-suggestions/copilot-page-suggestions.component';
import { CopilotPageSummaryComponent } from '../../copilot/copilot-page-summary/copilot-page-summary.component';
import { CopilotChatController } from '../../copilot/services/copilot-chat-controller';
import { CopilotPageSuggestionsService } from '../../copilot/services/copilot-page-suggestions.service';
import { CopilotPageSummaryService } from '../../copilot/services/copilot-page-summary.service';
import { CopilotSidePanelService } from '../../copilot/services/copilot-side-panel.service';
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';
import { isPure1Production } from '../../utils/url';

const FIRST_LOAD_DELAY = 100;
const PROACTIVE_RECOMMENDATIONS_REFRESH_INTERVAL_MS = moment.duration(300, 'seconds').asMilliseconds();
// Matches hive-app-navigation's `transition: width 0.3s`.
const RAIL_TRANSITION_MS = 300;

/** Make the org switcher element disabled (readonly) on certain pages. */
export const ORG_SWITCHER_READONLY_TAB_IDS = ['tab-reporting', 'tab-entitlements'];

const IS_ACTIVE_OPTIONS: IsActiveMatchOptions = {
    matrixParams: 'ignored',
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
};

@Component({
    selector: 'page-wrapper',
    templateUrl: 'page-wrapper.component.html',
    host: {
        id: 'wrapper',
    },
    providers: [
        NavMenuIdPipe,
        provideMarkdown({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
        }),
    ],
    imports: [
        RootDirective,
        AppRailComponent,
        TooltipDirective,
        SvgDirective,
        RouterOutlet,
        PureToastComponent,
        ExportOverlayComponent,
        FeedbackButtonComponent,
        NgClass,
        FeedbackFormComponent,
        CopilotOnboardingModalComponent,
        CopilotPageSuggestionsComponent,
        CopilotPageSummaryComponent,
        CopilotPromptLinkComponent,
        CopilotSidePanelComponent,
        AuthorizerModule,
        AsyncPipe,
    ],
})
export class PageWrapperComponent implements OnInit, OnDestroy {
    showFeedbackButton = false;
    pageTitle = '';
    pageHelpUrl = '';
    fullBleed = false;
    copilotPromptLinks: CopilotPromptLinkData[] = [];

    tabs: IMainTab[] = [];
    tabsToRender: IMainTab[] = [];
    arrayCounts: IConnectedCount;
    eolApplianceCount: number;
    showEolApplianceBadge = false;
    showEolOrEoscApplianceBadge = false;
    eolOrEoscApplianceCount: number;
    eolOrEoscApplianceTooltip: string;
    eoscApplianceCount: number;
    showArrayCountBadge = false;
    expiredSupportContractCount: number;
    openAlertsCount = 0;
    alertsEmphasisBadge: BadgeEmphasis;
    activeTabId: string;
    currentUser: CurrentUser = null;

    isVmAssessmentsBetaEnabled$: Observable<boolean>;
    isEOSCEnabled = false;
    showHiddenArraysEnabled$: Observable<boolean>;
    protected copilotSidePanelEnabled$: Observable<boolean>;
    protected copilotSidePanelQuestionsEnabled$: Observable<boolean>;
    protected copilotSidePanelQuestionsEnabled = false;
    protected copilotSidePanelSummarizeEnabled$: Observable<boolean>;
    protected copilotSidePanelSummarizeEnabled = false;
    protected isCopilotRoute$: Observable<boolean>;

    proRecOpaCheck = false;
    hasProRecFeature = false;
    recommendationCount: number;
    isOrgSwitcherReadonly = false;
    activeNavigation: 'pure1' | 'draas' = 'pure1';
    activeDraasClusterId: string | null = null;

    @HostBinding('class.export-overlay-enabled') hasActiveExports = false;

    private readonly destroy$ = new Subject<void>();
    private readonly toastService = inject(ToastService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly orgSwitcherService = inject(OrgSwitcherService);
    private readonly rail = viewChild(AppRailComponent);

    protected readonly showOrgSwitcher$ = combineLatest([
        this.orgSwitcherService.userOrgs$.pipe(
            map(orgs => orgs.length > 1),
            startWith(false),
        ),
        this.orgSwitcherService.isLoading$,
    ]).pipe(map(([hasMoreThanOneOrg, isLoading]) => hasMoreThanOneOrg || isLoading));

    // This template runs on every tick of every page, so it binds values that are built once.
    // getNotificationCenterCounter() returns a new zip() on each call, and the isAllowedImpure pipe asks
    // AuthorizationService.isAllowed() for a new pipeline on each check: either way the async pipe tore
    // down its subscription and resubscribed on every tick.
    private readonly notificationCounters = inject(NotificationCenterCounterService);
    protected readonly notificationCount$ = combineLatest([
        this.notificationCounters.unreadNotificationCounter$,
        this.notificationCounters.mpaRequestsCounter$,
    ]).pipe(map(([notifications, mpaRequests]) => notifications + mpaRequests));
    protected readonly canReadSupportKnowledge = isAllowed('PURE1:read:support_knowledge');
    protected readonly canReadSupportCommunity = isAllowed('PURE1:read:support_community');

    constructor(
        private readonly touService: TouService,
        public supportCaseOrchestrator: SupportCaseOrchestratorService,
        private readonly cachedCurrentUserService: CachedCurrentUserService,
        @Inject(WINDOW) private readonly window: Window,
        private readonly featureFlagDxpService: FeatureFlagDxpService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly tabsService: TabsService,
        private readonly unifiedArrayService: UnifiedArrayService,
        private readonly navMenuIdPipe: NavMenuIdPipe,
        private readonly authorizationService: AuthorizationService,
        private readonly incidentService: IncidentService,
        private readonly alertsCountService: AlertsCountService,
        private readonly recommendationProcessingService: RecommendationProcessingService,
        protected readonly copilotSidePanelService: CopilotSidePanelService,
        protected readonly copilotChatController: CopilotChatController,
        private readonly copilotPageSuggestionsService: CopilotPageSuggestionsService,
        private readonly copilotPageSummaryService: CopilotPageSummaryService,
    ) {}

    ngOnInit(): void {
        this.orgSwitcherService.loadOrgSwitcherItems();

        this.cachedCurrentUserService
            .get()
            .pipe(take(1))
            .subscribe(currentUser => {
                this.currentUser = currentUser;
            });

        // Slightly delay a lot of init calls to give a chance to the main content to render before counts, reports, …
        this.window.setTimeout(() => {
            this.touService.run().subscribe();
        }, FIRST_LOAD_DELAY);

        this.isVmAssessmentsBetaEnabled$ = this.featureFlagDxpService
            .getFeatureFlag(FeatureNames.VM_ASSESSMENT_BETA_BANNER)
            .pipe(map(feature => feature?.enabled === true));

        // Same decision the Resources page takes for its card list -- the counts here must not
        // include an archived appliance the list below them excludes (CLOUD-263296).
        this.showHiddenArraysEnabled$ = this.featureFlagDxpService.getFeatureFlag(FeatureNames.SHOW_HIDDEN_ARRAYS).pipe(
            map(feature => feature?.enabled === true),
            shareReplay(1),
        );

        this.featureFlagDxpService.getFeatureFlag(FeatureNames.EOC).subscribe(feature => {
            this.isEOSCEnabled = feature?.enabled;
        });
        this.copilotSidePanelEnabled$ = combineLatest([
            this.featureFlagDxpService.getFeatureFlag(FeatureNames.COPILOT),
            this.featureFlagDxpService.getFeatureFlag(FeatureNames.COPILOT_SIDE_PANEL),
        ]).pipe(map(([copilot, panel]) => copilot?.enabled === true && panel?.enabled === true));

        this.copilotSidePanelQuestionsEnabled$ = combineLatest([
            this.copilotSidePanelEnabled$,
            this.featureFlagDxpService.getFeatureFlag(FeatureNames.GENAI_ASK_ENDPOINT),
        ]).pipe(
            map(([sidePanelEnabled, questions]) => sidePanelEnabled && questions?.enabled === true),
            shareReplay(1),
        );

        this.copilotSidePanelSummarizeEnabled$ = combineLatest([
            this.copilotSidePanelEnabled$,
            this.featureFlagDxpService.getFeatureFlag(FeatureNames.COPILOT_SIDE_PANEL_SUMMARIZE),
        ]).pipe(
            map(([sidePanelEnabled, summarize]) => sidePanelEnabled && summarize?.enabled === true),
            shareReplay(1),
        );

        const isOnCopilot = (): boolean => this.orgStrippedUrl.split('?')[0] === '/copilot';
        this.isCopilotRoute$ = this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(() => isOnCopilot()),
            startWith(isOnCopilot()),
        );

        this.copilotSidePanelEnabled$.pipe(filter(Boolean), take(1), takeUntil(this.destroy$)).subscribe(() => {
            // The controller is a root-scoped singleton shared with CopilotViewComponent,
            // so an in-flight chat from the side panel keeps streaming into the same
            // conversation when the user clicks "expand" and navigates to /copilot.
            this.copilotChatController.init();
            this.copilotChatController.loadConversations();
            // The side panel holds an open back until the user has accepted the welcome modal;
            // resolving that state now keeps the wait off their first click.
            this.copilotSidePanelService.prefetchOnboardingState();

            // The panel service decides whether a prompt starts a fresh conversation and has
            // already done so by the time it reaches here; this only sends the message.
            this.copilotSidePanelService.promptRequests$.pipe(takeUntil(this.destroy$)).subscribe(prompt => {
                this.copilotChatController.submitMessage(prompt);
            });
        });

        this.copilotSidePanelQuestionsEnabled$.pipe(takeUntil(this.destroy$)).subscribe(enabled => {
            this.copilotSidePanelQuestionsEnabled = enabled;
            this.cdr.markForCheck();
            if (enabled) {
                this.copilotPageSuggestionsService.start();
            }
        });

        this.copilotSidePanelSummarizeEnabled$.pipe(takeUntil(this.destroy$)).subscribe(enabled => {
            this.copilotSidePanelSummarizeEnabled = enabled;
            this.cdr.markForCheck();
            if (enabled) {
                this.copilotPageSummaryService.start();
            }
        });

        // Load the tabs
        this.tabsService
            .build$(MENU)
            .pipe(takeUntil(this.destroy$))
            .subscribe(tabs => {
                this.tabs = [...tabs];
                this.updateRenderedTabs();
                this.updateActiveTabs();
            });

        // To support browser navigation and such, we don't rely on the sidenav's output event
        // to tell us what tab is active. Instead, hook into the router events to update the active tab id on route change.
        this.router.events
            .pipe(
                filter(
                    e => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.updateActiveTabs();
                // A click sets Hive's active item before a guard can cancel, and the binding cannot see this reset.
                this.rail()?.resetActiveItem(this.activeTabId);
            });

        // Regularly update connected arrays and expired support contract counts
        const requestFields: getArraysFields[] = [
            'appliance_id',
            'is_current',
            'last_updated',
            'contract_status',
            'evergreen_tier',
            'has_end_of_life_hardware',
        ]; // Only request the subset of fields that we need

        this.showHiddenArraysEnabled$
            .pipe(
                switchMap(showHiddenArraysEnabled =>
                    smartTimer(0, moment.duration(30, 'seconds').asMilliseconds()).pipe(
                        exhaustMap(() =>
                            recursiveListItems(
                                this.unifiedArrayService,
                                {
                                    fields: requestFields,
                                    supportStatusFilterOption: showHiddenArraysEnabled
                                        ? 'include_hidden_arrays_in_courtesy_period'
                                        : 'include_arrays_in_courtesy_period',
                                },
                                'appliance_id',
                                array => array.appliance_id,
                            ).pipe(
                                catchError(err => {
                                    console.warn('Sidenav: failed to update arrays for counts', err);
                                    return of(null);
                                }),
                            ),
                        ),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(result => {
                this.updateSummaryBadges(result);
            });

        // Proactive recommendations
        this.authorizationService
            .isAllowed('PURE1:navigate:proactive_recommendations')
            .pipe(
                tap(isAllowed => {
                    this.proRecOpaCheck = isAllowed;
                }),
                filter(isAllowed => isAllowed),
                switchMap(() => {
                    this.hasProRecFeature = true;
                    return this.getOpenIncidentCount();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe(data => {
                if (data != null) {
                    this.recommendationCount = data;
                }
            });

        // alerts badge logic
        this.alertsCountService
            .getOpenAlerts()
            .pipe(takeUntil(this.destroy$))
            .subscribe(alerts => {
                alerts.response.forEach(item => {
                    this.openAlertsCount = this.openAlertsCount + item.count;
                    if (item.currentSeverity === 'critical') {
                        this.alertsEmphasisBadge = 'major';
                    } else if (item.currentSeverity === 'warning') {
                        if (this.alertsEmphasisBadge !== 'major') {
                            this.alertsEmphasisBadge = 'warning';
                        }
                    } else if (item.currentSeverity === 'info') {
                        if (this.alertsEmphasisBadge !== 'major' && this.alertsEmphasisBadge !== 'warning') {
                            this.alertsEmphasisBadge = 'info';
                        }
                    }
                });
            });

        // The NavigationSkipped event is here due to CLOUD-124234. This may be a bug in the interaction with the Angular Router and UIRouter.
        // Having the NavigationSkipped here doesn't actually hurt anything since we always updated based on the current state, it just
        // means we update more than we might need to otherwise. It may no longer be needed once we fully remove UIRouter.
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(e => e instanceof NavigationEnd || e instanceof NavigationSkipped),
            )
            .subscribe((e: NavigationEnd | NavigationSkipped) => this.onRouteChangeEnd(e));

        // Kick off initial callback since router.events won't trigger on the first page load.
        // Without this, the page title / helpUrl won't get set correctly on page load.
        this.onRouteChangeEnd(null);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    onActiveExportsChanged(active: boolean): void {
        this.hasActiveExports = active;
    }

    handleClickProRecIcon(): void {
        this.router.navigate(['/services/servicecatalog/recommendationsview']);
    }

    protected backToPure1(): void {
        this.router.navigateByUrl('/resilience/deployments');
    }

    /**
     * This determines which tabs will be rendered based on the current active navigation.
     */
    updateRenderedTabs(): void {
        // DRaaS cluster-specific routes contain the cluster ID in the URL.
        // We need to extract it in order to render the submenu tabs' paths correctly.
        if (this.orgStrippedUrl.startsWith('/resilience/deployments/c')) {
            this.activeDraasClusterId = this.orgStrippedUrl.split('/')[3];
        } else {
            this.activeDraasClusterId = null;
        }

        switch (this.activeNavigation) {
            case 'draas':
                // DRaaS submenu paths need to be rendered dynamically based on the active cluster ID
                this.tabsToRender = this.tabs
                    .filter(tab => tab.submenu === 'draas')
                    .map(tab => ({
                        ...tab,
                        path: tab.path.replace(':clusterId', this.activeDraasClusterId),
                        subTabs: tab.subTabs.map(subTab => ({
                            ...subTab,
                            path: subTab.path.replace(':clusterId', this.activeDraasClusterId),
                        })),
                    }));
                break;

            case 'pure1':
            default:
                this.tabsToRender = this.tabs.filter(tab => tab.submenu === undefined);
                break;
        }
    }

    /**
     * This handles Angular router navigation
     */
    private onRouteChangeEnd(e: NavigationEnd | NavigationSkipped): void {
        let currentRoute = this.route.root;
        const { snapshot } = currentRoute;
        let data = snapshot.data;
        this.pageTitle = snapshot.title;
        while (currentRoute.children[0] != null) {
            currentRoute = currentRoute.children[0];
            if (currentRoute.snapshot) {
                if (currentRoute.snapshot.title) {
                    this.pageTitle = currentRoute.snapshot.title;
                }
                data = {
                    ...data,
                    ...currentRoute.snapshot.data,
                };
            }
        }

        this.showFeedbackButton = data.showFeedbackButton;

        if (data.pageHelpUrlHidingFeatureFlag) {
            this.featureFlagDxpService.getFeatureFlag(data.pageHelpUrlHidingFeatureFlag).subscribe(feature => {
                if (feature?.enabled !== true) {
                    this.pageHelpUrl = data.pageHelpUrl;
                }
            });
        } else {
            this.pageHelpUrl = data.pageHelpUrl;
        }

        this.copilotPromptLinks = data.copilotPromptLinks ?? [];

        this.fullBleed = data.fullBleed === true;

        switch (data.navSubmenu) {
            case 'draas':
                this.activeNavigation = 'draas';
                break;
            default:
                this.activeNavigation = 'pure1';
        }

        this.updateRenderedTabs();
    }

    private updateSummaryBadges(arrays: UnifiedArray[]): void {
        if (arrays == null) {
            return; // If null due to error / invalid data, don't change anything
        }

        this.updateApplianceLifecycleCounts(arrays);
        this.updateArrayCounts(arrays);
        this.updateApplianceBadgeVisibility();
    }

    private updateApplianceLifecycleCounts(arrays: UnifiedArray[]): void {
        if (!this.isEOSCEnabled) {
            this.expiredSupportContractCount = arrays.filter(
                array =>
                    (array.subscription_type === SubscriptionType.FOUNDATION ||
                        array.subscription_type === SubscriptionType.FOREVER) &&
                    array.contract_status === ArrayContractStatus.EXPIRED,
            ).length;
        }
        this.eolApplianceCount = arrays.filter(array => array.has_end_of_life_hardware).length;
        if (this.isEOSCEnabled) {
            this.eoscApplianceCount = arrays.filter(
                array => array.contract_status === ArrayContractStatus.EXPIRED,
            ).length;
            const eolOrEoscApplianceCount = arrays.filter(
                array => array.has_end_of_life_hardware || array.contract_status === ArrayContractStatus.EXPIRED,
            ).length;
            if (this.eolApplianceCount > 0 && this.eoscApplianceCount > 0) {
                this.eolOrEoscApplianceCount = eolOrEoscApplianceCount;
                this.eolOrEoscApplianceTooltip = `where the hardware is going End of Life or Evergreen Subscription Support Contract has expired.`;
            } else if (this.eoscApplianceCount > 0) {
                this.eolOrEoscApplianceCount = this.eoscApplianceCount;
                this.eolOrEoscApplianceTooltip = `where Evergreen Subscription Support Contract has expired.`;
            } else {
                this.eolOrEoscApplianceCount = this.eolApplianceCount;
                this.eolOrEoscApplianceTooltip = `where the hardware is going End of Life.`;
            }
        }
    }

    private updateArrayCounts(arrays: UnifiedArray[]): void {
        const connected = arrays.filter(array => array.isCurrent).length;
        const disconnected = arrays.length - connected;
        // The rail is OnPush: a new object every poll would re-render it every 30 s, so keep the old one when unchanged.
        if (this.arrayCounts?.connected !== connected || this.arrayCounts?.disconnected !== disconnected) {
            this.arrayCounts = { connected, disconnected };
        }
    }

    /**
     * For Appliances, we have two badges, but only space to show one at a time.
     * If a user has both the EOL/EOSC (if EOSC feature flag enabled) and Array Count badges needing to be shown,
     * only show one of them, but cycle through which one gets shown, with the EOL/EOSC shown first.
     */
    private updateApplianceBadgeVisibility(): void {
        const showArrayCount = this.arrayCounts.disconnected > 0;
        if (this.isEOSCEnabled) {
            const [showEolOrEosc, showArrayCountNow] = this.alternateBadges(
                this.eolOrEoscApplianceCount > 0,
                showArrayCount,
                this.showEolOrEoscApplianceBadge,
            );
            this.showEolOrEoscApplianceBadge = showEolOrEosc;
            this.showEolApplianceBadge = this.eolApplianceCount > 0;
            this.showArrayCountBadge = showArrayCountNow;
        } else {
            const [showEol, showArrayCountNow] = this.alternateBadges(
                this.eolApplianceCount > 0,
                showArrayCount,
                this.showEolApplianceBadge,
            );
            this.showEolApplianceBadge = showEol;
            this.showArrayCountBadge = showArrayCountNow;
        }
    }

    /** Only one of the two badges fits: while both apply, alternate between them, starting with the first. */
    private alternateBadges(showFirst: boolean, showSecond: boolean, firstIsShowing: boolean): [boolean, boolean] {
        if (showFirst && showSecond) {
            return firstIsShowing ? [false, true] : [true, false];
        }
        return [showFirst, showSecond];
    }

    private updateActiveTabs(): void {
        const isActiveParams = IS_ACTIVE_OPTIONS;

        const activeTab = flatMap(this.tabs.map(tab => tab.subTabs))
            .flatMap(tabs => tabs.subTabs ?? tabs) // This is adding another level of subtabs.
            .concat(this.tabs)
            .filter(tab => tab?.path != null)
            .find(tab => {
                if (
                    tab.path === '/services/servicecatalog/main' &&
                    this.router.isActive(this.makeActivePath('/services/servicecatalog/details'), isActiveParams)
                ) {
                    // Highlight the main tab when on the details page
                    return true;
                } else if (this.orgStrippedUrl.startsWith('/resilience/deployments/c')) {
                    // DRaaS pages that are cluster-specific contain the cluster ID in the URL.
                    // In order to highlight the correct tab, we need to replace the placeholder with the actual cluster ID when checking if the tab is active.
                    // DRaaS cluster ID always starts with 'c'.
                    return (
                        tab.path.startsWith('/resilience/deployments/:clusterId') &&
                        this.router.isActive(
                            this.makeActivePath(tab.path.replace(':clusterId', this.activeDraasClusterId)),
                            isActiveParams,
                        )
                    );
                } else {
                    return this.router.isActive(this.makeActivePath(tab.path), isActiveParams);
                }
            });

        const activeTabId = this.navMenuIdPipe.transform(activeTab);
        // Hive moves an active section onto its first child, so a section id cannot be set. A page that only
        // matches a section (e.g. a redirect target like /workflows/requestaccess) keeps the previous item instead.
        const isSectionOnly = activeTab != null && activeTab.subTabs?.length > 0;
        if (!isSectionOnly) {
            this.activeTabId = activeTabId;
        }

        // Readonly follows the route's own match, not the kept previous item.
        this.isOrgSwitcherReadonly =
            ORG_SWITCHER_READONLY_TAB_IDS.includes(activeTabId) ||
            this.router.isActive(this.makeActivePath('/notifications'), isActiveParams);
    }

    /** router.url with /orgs/<uid> prefix stripped — for string comparisons that must work in both FF states. */
    private get orgStrippedUrl(): string {
        return stripLeadingOrgUid(this.router.url);
    }

    /** Prefixes path with /orgs/<uid> when the current URL is org-scoped so router.isActive() matches. */
    private makeActivePath(path: string): string {
        const uid = extractOrgUidFromPath(this.router.url);
        return uid ? '/orgs/' + uid + path : path;
    }

    private getOpenIncidentCount(): Observable<number> {
        return smartTimer(0, PROACTIVE_RECOMMENDATIONS_REFRESH_INTERVAL_MS).pipe(
            exhaustMap(() =>
                combineLatest([
                    this.incidentService.list().pipe(
                        take(1),
                        catchError(err => {
                            console.error('Failed to update incident count', err);
                            return of(null);
                        }),
                    ),
                    this.featureFlagDxpService.getFeatureFlag(FeatureNames.PROREC_QUOTE_CONTRACT_RENEWAL_GUI).pipe(
                        take(1),
                        map(flag => flag?.enabled === true),
                    ),
                    this.featureFlagDxpService.getFeatureFlag(FeatureNames.PROREC_EVERCYCLE_BUYBACK).pipe(
                        take(1),
                        map(flag => flag?.enabled === true),
                    ),
                ]),
            ),
            map(([data, isQuoteContractRenewalGuiEnabled, isEvercycleBuybackEnabled]) => {
                if (!data?.response) {
                    return 0;
                }
                // Use the processRecommendations service to get the actual renderable count
                const result = this.recommendationProcessingService.processRecommendations(
                    data.response,
                    isQuoteContractRenewalGuiEnabled,
                    isEvercycleBuybackEnabled,
                );
                return result.renderableRecommendations.length;
            }),
        );
    }

    protected onSidePanelExpand(): void {
        this.router.navigate(['/copilot'], {
            state: { sessionId: this.copilotChatController.conversation?.sessionId },
        });
    }

    protected onCollapseToggle(collapsed: boolean): void {
        if (collapsed) {
            ampli.collapseLeftSideNavigationClicked();
        } else {
            ampli.expandLeftSideNavigationClicked();
        }
        // Highcharts reflows on window resize; a timer, not transitionend, because reduced motion drops the transition.
        this.window.setTimeout(() => this.window.dispatchEvent(new Event('resize')), RAIL_TRANSITION_MS + 10);
    }

    protected onAppSwitcherVisibilityChange(visible: boolean): void {
        if (visible) {
            ampli.appSwitcherClicked();
        }
    }

    protected onAppSwitcherAppClick(app: AppSwitcherApp): void {
        ampli.appSwitcherClicked({ appId: app.id, appName: app.label });
    }

    protected handleHelpItemClick(item: string): void {
        if (item === 'help with this page') {
            ampli.helpClicked({
                'help item': item,
                'help path': this.router.url,
            });
        } else {
            ampli.helpClicked({
                'help item': item,
            });
        }
    }

    protected readonly getCommunityUrl = () => {
        if (isPure1Production(this.window.location)) {
            return 'https://community.purestorage.com/community?ref=true';
        } else {
            return 'https://puresapuat2.service-now.com/community';
        }
    };

    protected onOrgSwitchError(event: { message: string; cause: unknown }) {
        console.error(event.cause);
        this.toastService.addToast({
            message: event.message,
        });
    }
}
